import { auth } from '@/auth';
import { loadSystemLinkTitle } from './loadSystemLinkTitle';
import { loadDocumentLinkTitles, DOCUMENT_LINK_TITLE_BATCH_SIZE } from './loadDocumentLinkTitles';
import type { SystemLinkLookup } from '@/utils/resolveSystemLink';

type Listener = (title: string | undefined) => void;
interface IBatch {
    controller: AbortController;
    entries: IEntry[];
    cancelTimer?: ReturnType<typeof setTimeout>;
}
interface IEntry {
    lookup: SystemLinkLookup;
    listeners: Set<Listener>;
    controller: AbortController;
    state: 'queued' | 'loading' | 'ready';
    title?: string;
    expires: number;
    batch?: IBatch;
}

export const createSystemLinkTitleCache = (
    load: typeof loadSystemLinkTitle,
    {
        concurrency = 4,
        capacity = 256,
        ttl = 60000,
        loadDocuments,
    }: {
        concurrency?: number;
        capacity?: number;
        ttl?: number;
        loadDocuments?: typeof loadDocumentLinkTitles;
    } = {},
) => {
    const entries = new Map<string, IEntry>();
    // Готовые записи без подписчиков в порядке ухода последнего подписчика: вытесняются только
    // они, поэтому обрезка не обходит тысячи живых ссылок большой таблицы на каждую публикацию.
    const idle = new Set<string>();
    // Отказ (таймаут, 5xx) помним недолго: иначе один сбой оставлял бы без названий целую пачку.
    const failureTtl = Math.min(ttl, 5000);
    let running = 0;
    let scheduled = false;

    const trim = () => {
        for (const key of idle) {
            if (idle.size <= capacity) break;
            idle.delete(key);
            entries.delete(key);
        }
    };
    const publish = (key: string, entry: IEntry, title: string | undefined, lifetime: number) => {
        if (entry.controller.signal.aborted || entries.get(key) !== entry) return;
        entry.state = 'ready';
        entry.title = title?.trim() || undefined;
        entry.expires = Date.now() + lifetime;
        entry.batch = undefined;
        entry.listeners.forEach((listener) => listener(entry.title));
        if (!entry.listeners.size) idle.add(key);
        trim();
    };
    const pump = () => {
        if (loadDocuments && running < concurrency) {
            const pending = [...entries].filter(
                ([, entry]) => entry.lookup.kind === 'document' && entry.state === 'queued' && entry.listeners.size,
            );
            for (
                let offset = 0;
                offset < pending.length && running < concurrency;
                offset += DOCUMENT_LINK_TITLE_BATCH_SIZE
            ) {
                const group = pending.slice(offset, offset + DOCUMENT_LINK_TITLE_BATCH_SIZE);
                const batch: IBatch = { controller: new AbortController(), entries: group.map(([, entry]) => entry) };
                group.forEach(([, entry]) => {
                    entry.state = 'loading';
                    entry.batch = batch;
                });
                running++;
                void loadDocuments(
                    group.map(([, entry]) => (entry.lookup as { id: string }).id),
                    batch.controller.signal,
                )
                    .then(
                        (titles) => ({ titles, lifetime: ttl }),
                        () => ({ titles: {} as Record<string, string>, lifetime: failureTtl }),
                    )
                    .then(({ titles, lifetime }) => {
                        group.forEach(([key, entry]) =>
                            publish(key, entry, titles[(entry.lookup as { id: string }).id], lifetime),
                        );
                    })
                    .finally(() => {
                        clearTimeout(batch.cancelTimer);
                        running--;
                        pump();
                    });
            }
        }
        for (const [key, entry] of entries) {
            if (running >= concurrency) break;
            if (entry.state !== 'queued' || !entry.listeners.size) continue;
            if (loadDocuments && entry.lookup.kind === 'document') continue;
            entry.state = 'loading';
            running++;
            void load(entry.lookup, entry.controller.signal)
                .then(
                    (title) => publish(key, entry, title, ttl),
                    () => publish(key, entry, undefined, failureTtl),
                )
                .finally(() => {
                    running--;
                    pump();
                });
        }
    };
    return {
        subscribe(lookup: SystemLinkLookup, listener: Listener) {
            const key = JSON.stringify(lookup);
            let entry = entries.get(key);
            if (entry?.state === 'ready' && entry.expires <= Date.now() && !entry.listeners.size) {
                idle.delete(key);
                entries.delete(key);
                entry = undefined;
            }
            if (!entry) {
                entry = {
                    lookup,
                    listeners: new Set(),
                    controller: new AbortController(),
                    state: 'queued',
                    expires: 0,
                };
                entries.set(key, entry);
            }
            const subscribed = entry;
            idle.delete(key);
            if (subscribed.batch) {
                clearTimeout(subscribed.batch.cancelTimer);
                subscribed.batch.cancelTimer = undefined;
            }
            subscribed.listeners.add(listener);
            if (subscribed.state === 'ready') listener(subscribed.title);
            if (loadDocuments && !scheduled) {
                // Все URL-ячейки одного commit React успевают подписаться до отправки пакета.
                scheduled = true;
                queueMicrotask(() => {
                    scheduled = false;
                    pump();
                });
            } else if (!loadDocuments) pump();
            return () => {
                subscribed.listeners.delete(listener);
                if (!subscribed.listeners.size && subscribed.state === 'ready' && entries.get(key) === subscribed) {
                    idle.add(key);
                } else if (!subscribed.listeners.size && subscribed.state !== 'ready') {
                    const batch = subscribed.batch;
                    if (batch) {
                        // Пересборка карты полей может на один commit размонтировать все URL-ячейки.
                        // Короткое окно позволяет повторному mount присоединиться к тому же запросу.
                        if (batch.entries.every((entry) => !entry.listeners.size) && !batch.cancelTimer) {
                            batch.cancelTimer = setTimeout(() => {
                                if (batch.entries.some((entry) => entry.listeners.size)) return;
                                batch.controller.abort();
                                for (const entry of batch.entries) {
                                    entry.controller.abort();
                                    const batchKey = JSON.stringify(entry.lookup);
                                    if (entries.get(batchKey) !== entry) continue;
                                    idle.delete(batchKey);
                                    entries.delete(batchKey);
                                }
                            }, 50);
                        }
                    } else {
                        subscribed.controller.abort();
                        if (entries.get(key) === subscribed) entries.delete(key);
                    }
                }
                trim();
            };
        },
        reset() {
            entries.forEach((entry) => {
                entry.controller.abort();
                entry.batch?.controller.abort();
                clearTimeout(entry.batch?.cancelTimer);
                entry.listeners.forEach((listener) => listener(undefined));
            });
            entries.clear();
            idle.clear();
        },
    };
};

// Кеш очищается вместе с авторизованной сессией; незавершённые ответы старого пользователя игнорируются.
export const systemLinkTitleCache = createSystemLinkTitleCache(loadSystemLinkTitle, {
    loadDocuments: loadDocumentLinkTitles,
});
auth.onSessionEnd(() => systemLinkTitleCache.reset());
